import { useEffect, useRef } from 'react'
import { useFormik } from 'formik'
import { enqueueSnackbar } from 'notistack'

import { ISSUE_RESPONSIBILITY } from '@img-arena/consoles-api-streaming'
import { Button, Grid, Paper, Stack, useTheme } from '@mui/material'
import { DateTimePicker } from '@/components/inputs/DateTimePicker/DateTimePicker'
import { FileUploadInput } from '@/components/inputs/FileUploadInput'
import { SelectBox } from '@/components/inputs/SelectBox'
import { ConsoleTextField } from '@/components/inputs/StyledInputComponents'
import { streamingRestApi } from '@/service/streamingApi'
import { onFieldChange } from '@/utils/forms/formik'

import { CasesByScope } from './CasesByScope'
import { ISSUE_FORM_LABELS, SCOPE_LABEL_MAP } from './constants'
import { issueFormValidationSchema } from './issueForm.validation'
import { EditableIssue, Scope } from './types'

const atLeastOneIsDefinied = (object: Record<string, unknown>) =>
  Object.values(object).some((value) => !!value)

type Props = {
  issue: EditableIssue
  onSubmit?: (issue: EditableIssue) => void
}

export const IssueForm = ({ issue, onSubmit }: Props) => {
  const endpoints = streamingRestApi.endpoints.other

  const theme = useTheme()
  const previousIssueRef = useRef<string>('')

  const formik = useFormik<EditableIssue>({
    initialValues: issue,
    validationSchema: issueFormValidationSchema,
    onSubmit: (issue, { setSubmitting, setTouched }) => {
      if (!issue.responsibility) return

      onSubmit?.(issue)

      setTouched({})
      setSubmitting(false)
    }
  })

  useEffect(() => {
    const issueKey = JSON.stringify(issue)
    if (previousIssueRef.current !== issueKey) {
      previousIssueRef.current = issueKey
      formik.resetForm({
        values: issue,
        touched: {},
        errors: {}
      })
    }
  }, [issue])

  const handleScopeChange = (scope: Scope) => {
    onFieldChange(formik, 'responsibility', scope)
  }

  const handleAttatchmentUpload = async (file: File | null) => {
    if (!file) return

    try {
      await endpoints.uploadAttachment(file).then((data) => {
        const attachment = {
          blobKey: data.data,
          contentType: file.type,
          fileName: file.name,
          size: file.size
        }

        onFieldChange(formik, 'attachment', null)
        onFieldChange(formik, 'attachment', attachment)

        enqueueSnackbar({
          variant: 'success',
          message: 'Upload successful.'
        })
      })
    } catch (error) {
      enqueueSnackbar({
        variant: 'error',
        message: 'Upload unsuccessful ' + error
      })
    }
  }

  const isOtherScope = formik.values.responsibility === ISSUE_RESPONSIBILITY.OTHER
  const casesRequired = isOtherScope
    ? formik.values.cases && atLeastOneIsDefinied(formik.values.cases)
    : true

  const saveButtonEnabled =
    formik.dirty &&
    Object.values(formik.touched).length > 0 &&
    !!formik.touched &&
    !!formik.isValid &&
    casesRequired

  return (
    <Grid data-testid="issue-form" container spacing={2} rowSpacing={1}>
      <Grid size={4}>
        <DateTimePicker
          label={ISSUE_FORM_LABELS.ISSUE_TIME}
          onChange={(value) => onFieldChange(formik, 'startDate', value)}
          value={formik.values.startDate}
          required
        />
      </Grid>

      <Grid size={4}>
        <ConsoleTextField
          type="number"
          label={ISSUE_FORM_LABELS.LOSS_OF_GAME_PLAY}
          value={formik.values.lossOfGamePlay}
          onChange={(e) => {
            const value = Number(e.target.value)

            if (value < 0) return
            onFieldChange(formik, 'lossOfGamePlay', value)
          }}
        />
      </Grid>

      <Grid offset={0.01} size={4}>
        <SelectBox
          formatListOption={(option) => SCOPE_LABEL_MAP[option]}
          formatInputOption={(option) => SCOPE_LABEL_MAP[option]}
          value={formik.values.responsibility}
          onChange={handleScopeChange}
          options={Object.values(ISSUE_RESPONSIBILITY).sort()}
          label={ISSUE_FORM_LABELS.ISSUE_SCOPE}
          required
          fullWidth
          error={formik.touched.responsibility ? formik.errors.responsibility : undefined}
        />
      </Grid>

      <CasesByScope
        cases={issue.cases}
        scope={formik.values.responsibility}
        onChange={(cases) => onFieldChange(formik, 'cases', cases)}
      />

      <Grid size={12}>
        <ConsoleTextField
          multiline
          required
          minRows={7}
          value={formik.values.problemDescription}
          maxRows={7}
          onChange={(e) => onFieldChange(formik, 'problemDescription', e.target.value)}
          label={ISSUE_FORM_LABELS.DESCRIPTION}
          error={formik.touched.problemDescription ? formik.errors.problemDescription : undefined}
          errorSx={{
            mt: -2
          }}
          sx={{
            width: '100%',
            height: '100%'
          }}
        />
      </Grid>

      <Grid size={12}>
        <Paper
          sx={{
            p: 2,
            my: 1,
            backgroundColor: theme.palette.card.header.backgroundColor
          }}
          elevation={0}
        >
          <FileUploadInput
            onChange={handleAttatchmentUpload}
            onClear={() => onFieldChange(formik, 'attachment', null)}
          />
        </Paper>
      </Grid>

      <Grid size={12}>
        <Stack direction="row" alignItems="center" justifyContent="flex-end">
          <Button onClick={formik.submitForm} disabled={!saveButtonEnabled}>
            Save
          </Button>
        </Stack>
      </Grid>
    </Grid>
  )
}
